import { Link } from "react-router-dom";
import { Lightning } from "@phosphor-icons/react";
import BrandLogo from "@/components/BrandLogo";

const Terms = () => (
  <div className="min-h-screen bg-[#0A0D14] text-white noise" data-testid="terms-page">
    <header className="border-b border-white/10 sticky top-0 bg-[#0A0D14]/95 backdrop-blur-md z-20">
      <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-2" data-testid="terms-logo">
          <BrandLogo height={52} />
        </Link>
        <Link to="/" className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] hover:text-white" data-testid="terms-back">← BACK</Link>
      </div>
    </header>

    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-3">— LEGAL</div>
      <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-8">Terms of Service</h1>
      <div className="text-xs font-mono-k text-[#A1A1AA] mb-10">Last updated: February 2026</div>

      <div className="space-y-8 text-[#DDDDDD] font-sans leading-relaxed">
        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">1. Acceptance</h2>
          <p>By creating an account or using Korporate ("Service"), you agree to these Terms of Service. If you don't agree, don't use the Service. You must be at least 16 years old and legally able to enter a contract.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">2. Your account</h2>
          <p>You are responsible for keeping your password and 2FA codes safe. You are responsible for everything that happens on your account. Notify us immediately at <a href="mailto:support@korporatelinks.com" className="text-[#FACC15] hover:underline">support@korporatelinks.com</a> if you suspect unauthorised access.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">3. Plans, billing, and refunds</h2>
          <p>Korporate offers a Free plan and a Pro plan billed at $19/month via PayPal, credit/debit card, or crypto. Subscriptions renew monthly until cancelled. You can cancel any time from your dashboard — access continues until the end of the paid period. Because Pro is a digital service delivered immediately, all fees are non-refundable except where required by law.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">4. Wallet and payouts</h2>
          <p>The Korporate wallet holds earnings from product sales and referral commissions. Withdrawal requests are queued for processing within 48 business hours. Payouts are made to the PayPal email or crypto wallet address you provide — Korporate is not responsible for funds sent to an incorrect address. Chargebacks or reversed transactions may result in balance clawbacks.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">5. Your content and shop</h2>
          <p>You own everything you upload — links, product files, images, bios. You grant Korporate a licence to host, display and deliver that content to your audience. You are solely responsible for the legality of what you sell. You may NOT use Korporate to distribute: illegal goods, weapons, drugs, adult sexual content, hate speech, malware, pirated material, MLM/pyramid schemes, or anything that violates a third party's rights.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">6. Acceptable use</h2>
          <p>Don't attack the platform, scrape at abusive rates, spam users, impersonate anyone, or interfere with other operators. We may throttle, suspend, or terminate accounts that break these rules with or without notice.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">7. Referral program</h2>
          <p>Referral commissions are paid when a referred user upgrades to Pro. Self-referrals, fake accounts, and incentivised sign-ups (paying people to join) are prohibited and will result in commission clawback and account termination.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">8. Third-party services</h2>
          <p>Korporate integrates with PayPal, NOWPayments, Resend, Anthropic Claude, Google, and social platforms (TikTok, LinkedIn, YouTube, Instagram, Facebook). Their terms apply when you connect them. We are not liable for outages or policy changes made by these providers.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">9. AI-generated content</h2>
          <p>The AI Copilot may generate keywords, copy, and campaign ideas. Outputs are provided as-is — review before publishing. You are responsible for how you use them and for compliance with advertising, tax, and consumer-protection laws in your region.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">10. Termination</h2>
          <p>You may delete your account any time from Settings. We may suspend or terminate accounts that violate these Terms, abuse the platform, or engage in fraud. Upon termination, we retain records required by law and delete the rest within 30 days.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">11. Disclaimers & liability</h2>
          <p>Korporate is provided "as is" without warranties of any kind. We are not liable for lost profits, lost data, or indirect damages. Our total liability for any claim is limited to the fees you paid us in the 12 months before the claim arose.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">12. Changes to these Terms</h2>
          <p>We may update these Terms as the product evolves. Material changes will be announced by email or in-app notice 14 days before they take effect. Continued use after the effective date means you accept the new Terms.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">13. Governing law</h2>
          <p>These Terms are governed by the laws of the jurisdiction where Korporate is registered, without regard to conflict-of-law rules. Disputes will be resolved in the courts of that jurisdiction unless mandatory local consumer law says otherwise.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl mb-3 text-[#FACC15]">14. Contact</h2>
          <p>Questions, notices, or takedown requests: <a href="mailto:support@korporatelinks.com" className="text-[#FACC15] hover:underline">support@korporatelinks.com</a>.</p>
        </section>
      </div>

      <div className="mt-16 pt-8 border-t border-white/10 text-xs font-mono-k tracking-widest-k text-[#A1A1AA] flex justify-between">
        <Link to="/privacy" className="hover:text-[#FACC15]" data-testid="terms-to-privacy">→ PRIVACY POLICY</Link>
        <Link to="/" className="hover:text-[#FACC15]" data-testid="terms-home">← HOME</Link>
      </div>
    </div>
  </div>
);

export default Terms;
